/**
 * Account state and the sync loop.
 *
 * Sync is pull-merge-push, always in that order, and always merging rather
 * than overwriting. The one invariant worth stating: this never replaces local
 * data with remote data. It combines them (see lib/sync/merge.ts) and pushes
 * the combination, so a device that has been offline for a month contributes
 * its work instead of losing it.
 *
 * Not persisted. Whether you are signed in is decided by a cookie the page
 * cannot read, so it is asked for on load rather than remembered — a stale
 * "signed in" flag in localStorage would show account UI to someone whose
 * session expired weeks ago.
 */

import { create } from "zustand";
import { pullState, pushState, VersionConflict } from "@/lib/sync/api.ts";
import { mergeStates, sameWatched } from "@/lib/sync/merge.ts";
import { readPersistedState, writeWholeState } from "./persistedState.ts";
import { useProgressStore } from "./progress.ts";
import { useSettingsStore } from "./settings.ts";

export type SyncStatus = "idle" | "syncing" | "synced" | "offline" | "error";

interface AccountState {
  email: string | null;
  status: SyncStatus;
  /** Last successful sync, for telling the reader when it last worked. */
  lastSyncedAt: number | null;
  /** Server version this device last agreed with. */
  version: number;
  message: string | null;

  setEmail: (email: string | null) => void;
  sync: () => Promise<void>;
}

/** One retry. A second conflict means a third device is racing, and the next
 *  sync will pick it up — better than looping against a moving target. */
const MAX_ATTEMPTS = 2;

export const useAccountStore = create<AccountState>()((set, get) => ({
  email: null,
  status: "idle",
  lastSyncedAt: null,
  version: 0,
  message: null,

  setEmail: (email) => set({ email, status: "idle", message: null }),

  sync: async () => {
    if (!get().email) return;
    set({ status: "syncing", message: null });

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        const remote = await pullState();
        const local = readPersistedState();

        // Nothing stored yet: this device's state becomes the starting point.
        const merged = remote.payload
          ? mergeStates(local, remote.payload)
          : { state: local, added: 0, updated: 0, removed: 0 };

        // Nothing to say. Pushing anyway would bump the version and hand every
        // other device a conflict on its next push, for a document identical to
        // the one already stored.
        if (remote.payload && sameWatched(merged.state, remote.payload)) {
          set({
            status: "synced",
            version: remote.version,
            lastSyncedAt: Date.now(),
            message: null,
          });
          return;
        }

        try {
          const version = await pushState(merged.state, remote.version);
          // Storage AND the live stores. Writing localStorage alone would
          // leave the road showing pre-merge data until the next reload,
          // which reads as "sync did nothing".
          writeWholeState(merged.state);
          // applyMerged, not applyImport("replace"): the merge already decided
          // what is deleted. Feeding it through the import path would treat
          // every entry this device had not heard of as a fresh un-watch and
          // tombstone it.
          useProgressStore.getState().applyMerged(merged.state.watched, merged.state.deleted);
          useSettingsStore.setState(merged.state.settings);
          set({
            status: "synced",
            version,
            lastSyncedAt: Date.now(),
            message: describe(merged.added, merged.updated, merged.removed),
          });
          return;
        } catch (err) {
          // Someone wrote between our pull and our push. Loop: pull again,
          // merge against what is now there, and try once more.
          if (err instanceof VersionConflict && attempt < MAX_ATTEMPTS) continue;
          throw err;
        }
      }
    } catch (err) {
      // Offline is not an error worth shouting about — this app is built to
      // work without a network, and sync resumes on its own next time.
      const offline = typeof navigator !== "undefined" && !navigator.onLine;
      set({
        status: offline ? "offline" : "error",
        message: offline
          ? "You're offline. Your progress is saved here and will sync later."
          : err instanceof Error
            ? err.message
            : "Sync failed.",
      });
    }
  },
}));

function describe(added: number, updated: number, removed: number): string | null {
  if (added === 0 && updated === 0 && removed === 0) return null;
  const bits: string[] = [];
  if (added > 0) bits.push(`${added} ${added === 1 ? "entry" : "entries"} merged in`);
  if (updated > 0) bits.push(`${updated} updated`);
  // Worth saying out loud. An entry vanishing because another device un-ticked
  // it is correct but startling if it happens silently.
  if (removed > 0) bits.push(`${removed} removed elsewhere`);
  return bits.join(", ");
}
